// SEO Metadata Configuration
export interface SEOData {
  title: string;
  description: string;
  keywords?: string[];
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  noindex?: boolean;
  nofollow?: boolean;
  jsonLd?: object;
}

// Default SEO configuration
export const defaultSEO: SEOData = {
  title: 'Wabisabi - Never Forget What Matters Most in Your Relationships',
  description: 'Wabisabi helps you remember the little things that matter most in your relationships. Join the waitlist for our relationship memory app that uses AI to help you be more thoughtful and present.',
  keywords: [
    'relationship app',
    'relationship memory',
    'thoughtful relationships',
    'remember relationships',
    'relationship AI',
    'mindful connections',
    'relationship tracker',
    'wabisabi app',
    'meaningful relationships',
    'relationship intelligence',
    'connection app',
    'relationship management'
  ],
  ogTitle: 'Wabisabi - Never Forget What Matters Most in Your Relationships',
  ogDescription: 'Join the waitlist for Wabisabi, the app that helps you remember what matters most about the people you care about. Using AI to build deeper, more meaningful connections.',
  ogImage: '/wabisabi-og-image.png',
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: 'Wabisabi - Never Forget What Matters Most',
  twitterDescription: 'The relationship memory app that helps you be more thoughtful and present. Join the waitlist today.',
  twitterImage: '/wabisabi-twitter-image.png'
};

// Page-specific SEO data
export const pageSEO: Record<string, SEOData> = {
  homepage: {
    title: 'Wabisabi - Relationship Memory App | Never Forget What Matters Most',
    description: 'Transform your relationships with Wabisabi, the AI-powered app that helps you remember preferences, check-ins, and meaningful moments. Join 1,000+ people on the waitlist for deeper connections.',
    keywords: [
      'relationship memory app',
      'relationship AI',
      'mindful relationships',
      'thoughtful connections',
      'remember preferences',
      'relationship intelligence',
      'wabisabi waitlist',
      'meaningful relationships app',
      'connection tracker',
      'relationship management'
    ],
    canonical: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Wabisabi',
      description: 'AI-powered relationship memory app that helps you remember what matters most about your relationships',
      applicationCategory: 'LifestyleApplication',
      operatingSystem: 'Web, iOS, Android',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free beta access for first 1,000 users'
      },
      author: {
        '@type': 'Organization',
        name: 'Wabisabi',
        url: 'https://wabisabi.app'
      }
    }
  },
  
  about: {
    title: 'About Wabisabi - The Story Behind Our Relationship Memory App',
    description: 'Learn the story behind Wabisabi and why we believe no one should mess up showing they care. Discover how our founder Atinder created an app to help you remember what matters most.',
    keywords: [
      'about wabisabi',
      'relationship app story',
      'wabisabi founder',
      'relationship memory mission',
      'thoughtful relationships',
      'mindful connections',
      'japanese philosophy wabisabi',
      'relationship intelligence'
    ],
    canonical: '/about',
    ogTitle: 'About Wabisabi - The Story Behind Thoughtful Relationships',
    ogDescription: 'Meet Atinder and learn how a simple moment changed everything. Discover why we built Wabisabi to help you remember what matters most in your relationships.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Wabisabi',
      description: 'The story behind Wabisabi relationship memory app',
      mainEntity: {
        '@type': 'Organization',
        name: 'Wabisabi',
        founder: {
          '@type': 'Person',
          name: 'Atinder'
        },
        mission: 'Help people remember what matters most in their relationships'
      }
    }
  },

  faq: {
    title: 'Wabisabi FAQ - Everything About Our Relationship Memory App Waitlist',
    description: 'Get answers to frequently asked questions about Wabisabi waitlist, beta access, pricing, privacy, and how our relationship memory app works. Join 1,000+ early users.',
    keywords: [
      'wabisabi faq',
      'relationship app questions',
      'wabisabi waitlist',
      'beta access',
      'relationship memory app help',
      'wabisabi pricing',
      'relationship app privacy',
      'thoughtful relationships faq'
    ],
    canonical: '/faq',
    ogTitle: 'Wabisabi FAQ - Waitlist & Beta Access Questions Answered',
    ogDescription: 'Everything you need to know about joining the Wabisabi waitlist and getting early access to our relationship memory app.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'When will Wabisabi launch?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We\'re launching our beta web app in July 2025 with 1,000 testing spots.'
          }
        },
        {
          '@type': 'Question',
          name: 'How does the waitlist work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Join the waitlist to secure your spot among the first 1,000 users. We\'ll notify you when beta access is ready.'
          }
        }
      ]
    }
  },

  contact: {
    title: 'Contact Wabisabi - Get in Touch About Our Relationship Memory App',
    description: 'Have questions about Wabisabi? Contact our team for support, feedback, or partnership inquiries. We\'d love to hear from you about building meaningful relationships.',
    keywords: [
      'contact wabisabi',
      'wabisabi support',
      'relationship app contact',
      'wabisabi team',
      'relationship app feedback',
      'partnership inquiries',
      'wabisabi help'
    ],
    canonical: '/contact',
    ogTitle: 'Contact Wabisabi - Let\'s Connect',
    ogDescription: 'Get in touch with the Wabisabi team. We\'re here to help you build deeper, more meaningful relationships.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Wabisabi',
      description: 'Contact page for Wabisabi relationship memory app',
      mainEntity: {
        '@type': 'Organization',
        name: 'Wabisabi',
        email: 'hello@appwabisabi.com',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'hello@appwabisabi.com'
        }
      }
    }
  },

  privacy: {
    title: 'Privacy Policy - How Wabisabi Protects Your Relationship Data',
    description: 'Learn how Wabisabi protects your personal and relationship data with enterprise-grade security, encryption, and privacy controls. Your data belongs to you.',
    keywords: [
      'wabisabi privacy policy',
      'relationship app privacy',
      'data protection',
      'relationship data security',
      'privacy controls',
      'data encryption',
      'user privacy rights'
    ],
    canonical: '/privacy-policy',
    noindex: true, // Typically don't index legal pages
    ogTitle: 'Wabisabi Privacy Policy - Your Data, Your Control',
    ogDescription: 'Comprehensive privacy policy explaining how Wabisabi protects your relationship data and personal information.'
  },

  terms: {
    title: 'Terms of Service - Wabisabi Relationship Memory App Usage Terms',
    description: 'Read the terms of service for using Wabisabi relationship memory app, including user rights, responsibilities, and service guidelines.',
    keywords: [
      'wabisabi terms of service',
      'relationship app terms',
      'user agreement',
      'service terms',
      'app usage policy'
    ],
    canonical: '/terms-of-service',
    noindex: true, // Typically don't index legal pages
    ogTitle: 'Wabisabi Terms of Service',
    ogDescription: 'Terms of service and user agreement for Wabisabi relationship memory app.'
  }
};

// Get SEO data for a specific page
export const getSEOData = (pageKey: string): SEOData => {
  const seoData = pageSEO[pageKey] || defaultSEO;
  
  // Merge with defaults to ensure all fields are present
  return {
    ...defaultSEO,
    ...seoData,
    keywords: seoData.keywords || defaultSEO.keywords,
  };
};

// Generate title with site name
export const generateTitle = (pageTitle: string, includeSiteName = true): string => {
  if (!includeSiteName) return pageTitle;
  
  // Don't double-add site name if already present
  if (pageTitle.toLowerCase().includes('wabisabi')) {
    return pageTitle;
  }
  
  return `${pageTitle} | Wabisabi`;
};

// Generate meta description with optimal length (150-160 characters)
export const optimizeMetaDescription = (description: string): string => {
  if (description.length <= 160) return description;
  
  // Truncate at word boundary near 160 chars
  const truncated = description.substring(0, 157);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > 140 ? `${truncated.substring(0, lastSpace)}...` : `${truncated}...`;
};

// Generate keywords string
export const generateKeywords = (keywords: string[]): string => {
  return keywords.join(', ');
};

// Get canonical URL
export const getCanonicalUrl = (path: string): string => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://wabisabi.app';
  return `${baseUrl}${path}`;
};